"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import type { CSSProperties } from "react"

import {
  FLOATING_REVEAL_CLASS,
  floatingRevealStyle,
} from "@lib/util/floating-reveal"

import { useMagneticPull } from "../hooks/use-magnetic-pull"
import styles from "../styles/immersive-shop.module.css"

/** Tre lineette che si trasformano in X via CSS (solo transform). */
function MenuIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path className={styles.galleryMenuBarTop} d="M5 7h14" />
      <path className={styles.galleryMenuBarMid} d="M5 12h14" />
      <path className={styles.galleryMenuBarBottom} d="M5 17h14" />
    </svg>
  )
}

/** Bag con spunta: conferma momentanea di un prodotto aggiunto al carrello. */
function BagCheckIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M20 12.5C19.9751 12.4136 19.9499 12.326 19.9244 12.2373C18.8875 8.63723 17.4956 7.5 13.4291 7.5H9.65019C5.74529 7.5 4.23479 8.48796 3.1549 12.2373C2.18223 15.6144 1.6959 17.3029 2.20436 18.6124C2.51576 19.4143 3.06862 20.1097 3.79294 20.6104C5.17171 21.5636 8.63187 22.0381 12 21.9976" />
      <path d="M7 8V6.36364C7 3.95367 9.01472 2 11.5 2C13.9853 2 16 3.95367 16 6.36364V8" />
      <path d="M14 19C14 19 15 19 16 21C16 21 19.1765 16 22 15" />
      <path d="M10.5 11H12.5" />
    </svg>
  )
}

/** Scroll (px) oltre il quale il logotipo viene nascosto. */
const LOGO_HIDE_SCROLL_Y = 24

/** Uscita lineette, entrata bag (280ms ciascuna) e 1s di permanenza. */
const CART_ADDED_DURATION_MS = 280 + 280 + 1000

/** Indice di reveal + posizione (1..3) del bottone nel menu mobile. */
const menuItemStyle = (index: number): CSSProperties =>
  ({ ...floatingRevealStyle(index), "--menu-i": index + 1 } as CSSProperties)

function AccountIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M19 21a7 7 0 0 0-14 0" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="7" />
      <path d="m16.25 16.25 4 4" />
    </svg>
  )
}

function CartIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M7.5 8V6.36364C7.5 3.95367 9.51472 2 12 2C14.4853 2 16.5 3.95367 16.5 6.36364V8" />
      <path d="M13.8816 7.5H10.1184C6.22973 7.5 4.7255 8.48796 3.6501 12.2373C2.68147 15.6144 2.19716 17.3029 2.70352 18.6124C3.01361 19.4143 3.56418 20.1097 4.28549 20.6104C6.8944 22.4216 16.9865 22.5043 19.7145 20.6104C20.4358 20.1097 20.9864 19.4143 21.2965 18.6124C21.8028 17.3029 21.3185 15.6144 20.3499 12.2373C19.3173 8.63723 17.9313 7.5 13.8816 7.5Z" />
      <path d="M11 11H13" />
    </svg>
  )
}

export default function DesktopFloatingActions({
  countryCode,
  cartQuantity,
  onSearchOpen,
}: {
  countryCode: string
  cartQuantity: number
  onSearchOpen: () => void
}) {
  const {
    ref: logoRef,
    onPointerMove: onLogoPointerMove,
    onPointerLeave: onLogoPointerLeave,
  } = useMagneticPull<HTMLAnchorElement>()

  // Menu mobile: sempre compresso al mount e a ogni cambio di pagina.
  const pathname = usePathname()
  const navRef = useRef<HTMLElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = useCallback(() => setMenuOpen(false), [])

  // Un aumento di cartQuantity = prodotto aggiunto: la bag sostituisce per un
  // attimo le lineette del toggle (visibile solo su mobile).
  const [cartAdded, setCartAdded] = useState(false)
  const previousQuantityRef = useRef(cartQuantity)
  const cartAddedTimerRef = useRef(0)

  useEffect(() => {
    const increased = cartQuantity > previousQuantityRef.current

    previousQuantityRef.current = cartQuantity

    if (!increased) {
      return
    }

    setCartAdded(true)
    window.clearTimeout(cartAddedTimerRef.current)
    cartAddedTimerRef.current = window.setTimeout(
      () => setCartAdded(false),
      CART_ADDED_DURATION_MS
    )
  }, [cartQuantity])

  useEffect(() => () => window.clearTimeout(cartAddedTimerRef.current), [])

  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  // Il logotipo sparisce appena la pagina viene scrollata; i bottoni restano.
  // setState con valore invariato non ri-renderizza: nessun costo per scroll.
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const syncScrolled = () => setScrolled(window.scrollY > LOGO_HIDE_SCROLL_Y)

    syncScrolled()
    window.addEventListener("scroll", syncScrolled, { passive: true })

    return () => window.removeEventListener("scroll", syncScrolled)
  }, [pathname])

  // Listener attivi solo mentre il menu è aperto.
  useEffect(() => {
    if (!menuOpen) {
      return
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false)
      }
    }

    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)

    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [menuOpen])

  return (
    <>
    <Link
      aria-label="Exellious, vai alla home"
      className={`${styles.galleryLogo} ${FLOATING_REVEAL_CLASS}`}
      data-hidden={scrolled}
      href={`/${countryCode}`}
      tabIndex={scrolled ? -1 : undefined}
      onPointerLeave={onLogoPointerLeave}
      onPointerMove={onLogoPointerMove}
      prefetch
      ref={logoRef}
      style={floatingRevealStyle(0)}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        alt="Exellious"
        decoding="async"
        draggable={false}
        height={92}
        src="/brand/exellious-logo.svg"
        width={790}
      />
    </Link>
    <nav
      aria-label="Azioni account e acquisti"
      className={styles.galleryActions}
      data-open={menuOpen}
      ref={navRef}
    >
      {/* Visibile solo su mobile: apre/chiude i tre bottoni sottostanti. */}
      <button
        aria-controls="gallery-actions-menu"
        aria-expanded={menuOpen}
        aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
        className={`${styles.galleryActionButton} ${styles.galleryMenuToggle} ${FLOATING_REVEAL_CLASS}`}
        data-added={cartAdded}
        onClick={() => setMenuOpen((open) => !open)}
        style={floatingRevealStyle(0)}
        type="button"
      >
        <MenuIcon />
        <BagCheckIcon />
      </button>

      <div className={styles.galleryMenuItems} id="gallery-actions-menu">
        <Link
          aria-label="Account"
          className={`${styles.galleryActionButton} ${styles.galleryMenuItem} ${FLOATING_REVEAL_CLASS}`}
          href={`/${countryCode}/account`}
          onClick={closeMenu}
          prefetch
          style={menuItemStyle(0)}
          title="Account"
        >
          <AccountIcon />
        </Link>

        <button
          aria-label="Search products"
          className={`${styles.galleryActionButton} ${styles.galleryMenuItem} ${FLOATING_REVEAL_CLASS}`}
          onClick={() => {
            closeMenu()
            onSearchOpen()
          }}
          style={menuItemStyle(1)}
          title="Cerca"
          type="button"
        >
          <SearchIcon />
        </button>

        <Link
          aria-label={`Carrello, ${cartQuantity} ${
            cartQuantity === 1 ? "articolo" : "articoli"
          }`}
          className={`${styles.galleryActionButton} ${styles.galleryMenuItem} ${FLOATING_REVEAL_CLASS}`}
          href={`/${countryCode}/cart`}
          onClick={closeMenu}
          prefetch
          style={menuItemStyle(2)}
          title="Carrello"
        >
          <CartIcon />
          <span aria-hidden="true" className={styles.galleryCartBadge}>
            {cartQuantity > 99 ? "99+" : cartQuantity}
          </span>
        </Link>
      </div>
    </nav>
    </>
  )
}
